import React, { useState, useEffect } from 'react';
import './App.css';
import ContactForm from './ContactForm';
import SEO from './SEO';
import CookieConsent from './CookieConsent';
import { 
  trackPhoneClick, 
  trackEmailClick, 
  trackSocialClick,
  trackNavigation,
  initScrollTracking,
  initTimeTracking
} from './analytics';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
	  initScrollTracking();
  initTimeTracking();
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, 
  []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="relative bg-slate-50 text-slate-800 overflow-x-hidden">
       {/* SEO Component */}
      <SEO 
        title={null} // null uses default title
        description="Freedom Contracting Group provides commercial construction excellence in Chicago. 20+ years experience, 1000+ projects completed. Get a free quote today!"
        keywords="commercial construction Chicago, general contractor Illinois, office building construction, industrial warehouse construction, healthcare facility construction"
        path="/"
        breadcrumbs={[
          { name: 'Home', url: '/' }
        ]}
      />
	  {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/98 backdrop-blur-md shadow-lg' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-32">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('home')}>
             <div className="flex-shrink-0 relative z-0">
                <img 
                  src="/logo.webp" 
                  alt="Freedom Contracting Group Logo" 
                  className="h-16 sm:h-24 md:h-28 w-auto object-contain" 
                />
              </div>
              <div className="min-w-0 flex-shrink">
                <h1 className="text-sm sm:text-xl md:text-2xl font-bold leading-tight">
                  <span className="text-red-600">Freedom</span>
                  <span className="text-blue-900"> Contracting Group</span>
                </h1>
                <p className="text-xs text-gray-600 font-medium">Built on American Values</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-blue-900 hover:text-red-600 font-medium transition-colors">Home</button>
              <button onClick={() => scrollToSection('services')} className="text-blue-900 hover:text-red-600 font-medium transition-colors">Services</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-blue-900 hover:text-red-600 font-medium transition-colors">Portfolio</button>
              
              {/* About Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setAboutDropdownOpen(true)}
                onMouseLeave={() => setAboutDropdownOpen(false)}
              >
                <button 
                  className="flex items-center gap-1 text-blue-900 hover:text-red-600 font-medium transition-colors py-2"
                  onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                >
                  About
                  <svg className={`w-4 h-4 transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                
                {aboutDropdownOpen && (
                  <div className="absolute top-full left-0 pt-2">
                    <div className="w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                      <a href="/our-team" 
                        onClick={() => {
                          setAboutDropdownOpen(false);
                          trackNavigation('team', 'about_dropdown');

                        }}
                        className="w-full text-left px-4 py-2 text-blue-900 hover:bg-blue-50 hover:text-red-600 font-medium transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                        Our Team
                      </a>
                      <a 
                        href="/culture"
                        className="w-full text-left px-4 py-2 text-blue-900 hover:bg-blue-50 hover:text-red-600 font-medium transition-colors flex items-center gap-2"
                        onClick={() => {
                          setAboutDropdownOpen(false);
                          trackNavigation('culture', 'about_dropdown');
                        }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                        </svg>
                        Culture & Giving
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <button 
                onClick={() => {
                  setContactModalOpen(true);
                  if (window.dataLayer) {
                    window.dataLayer.push({
                      event: 'contact_modal_opened',
                      page_title: 'Contact Us'
                    });
                  }
                }}
                className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl h-11 px-6 py-2 rounded-md font-medium transition-all"
              >
                Contact Us
              </button>
            </div>
            
            <button 
              className="md:hidden p-2 text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                )}
              </svg>
            </button>
          </div>
          
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('home')} className="text-left text-blue-900 hover:text-red-600 font-medium transition-colors py-2">Home</button>
                <button onClick={() => scrollToSection('services')} className="text-left text-blue-900 hover:text-red-600 font-medium transition-colors py-2">Services</button>
                <button onClick={() => scrollToSection('portfolio')} className="text-left text-blue-900 hover:text-red-600 font-medium transition-colors py-2">Portfolio</button>
                
                {/* Mobile About Dropdown */}
                <div>
                  <button 
                    onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                    className="w-full flex items-center justify-between text-blue-900 hover:text-red-600 font-medium transition-colors py-2"
                  >
                    About
                    <svg className={`w-4 h-4 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  
                  {mobileAboutOpen && (
                    <div className="pl-4 space-y-2 border-l-2 border-blue-200 ml-2">
                      <a href="/our-team" 
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileAboutOpen(false);
                          trackNavigation('team', 'mobile_menu');

                        }}
                        className="w-full text-left text-blue-900 hover:text-red-600 font-medium transition-colors py-2 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                        Our Team
                      </a>
                      <a 
                        href="/culture"
                        className="w-full text-left text-blue-900 hover:text-red-600 font-medium transition-colors py-2 flex items-center gap-2"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileAboutOpen(false);
                          trackNavigation('culture', 'mobile_menu');
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                        </svg>
                        Culture & Giving
                      </a>
                    </div>
                  )}
                </div>

                <button 
                  onClick={() => {
                    setContactModalOpen(true);
                    setMobileMenuOpen(false);
                    if (window.dataLayer) {
                      window.dataLayer.push({
                        event: 'contact_modal_opened',
                        page_title: 'Contact Us'
                      });
                    }
                  }}
                  className="text-center bg-gradient-to-r from-blue-900 to-blue-800 hover:from-red-600 hover:to-red-700 text-white font-medium py-3 px-6 rounded-md transition-all shadow-lg"
                >
                  Contact Us
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section with Parallax & Video Option */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* 
          OPTION 1: VIDEO BACKGROUND (Uncomment to use)
          Replace 'your-video.mp4' with your actual video file path
          Video should be: construction timelapse, drone footage, or team at work
        */}
        {/*
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/construction-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-blue-900/75 to-red-900/70"></div>
        </div>
        */}

        {/* OPTION 2: PARALLAX IMAGE (Current - Active) */}
        <div 
          className="absolute inset-0"
          style={{
            transform: isMobile ? 'none' : `translateY(${scrollY * 0.5}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          <img 
            src="/fcbghero1.webp" 
            alt="Construction background"
            className={`w-full h-full object-cover ${isMobile ? '' : 'scale-110'}`}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/70 to-red-900/60"></div>

        {/* Hero Content with Fade-in on Scroll */}
        <div 
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-white"
          style={isMobile ? {} : {
            opacity: Math.max(0, 1 - scrollY / 500),
            transform: `translateY(${scrollY * 0.3}px)`
          }}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight animate-fadeIn">
            Building America's Future
            <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-4 text-red-400">One Project at a Time</span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl mb-12 max-w-3xl mx-auto leading-relaxed text-gray-100 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            Commercial construction excellence with integrity, quality craftsmanship, and American values at our core.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center mb-16 animate-fadeIn px-4" style={{ animationDelay: '0.4s' }}>
            <button 
              onClick={() => {
                setContactModalOpen(true);
                if (window.dataLayer) {
                  window.dataLayer.push({
                    event: 'contact_modal_opened',
                    page_title: 'Contact Us',
                    button_location: 'Hero Section'
                  });
                }
              }}
              className="group inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-red-600 hover:text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all shadow-2xl hover:shadow-3xl transform hover:scale-105 text-sm sm:text-base"
            >
              Get a Free Quote
              <svg className="w-4 h-4 sm:w-5 sm:h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </button>

            <a 
              href="tel:+13126756000"
			  onClick={() => trackPhoneClick('312-675-6000', 'hero')}
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              <span className="hidden xs:inline">Call (312) 675-6000</span>
              <span className="xs:hidden">Call Now</span>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-4xl mx-auto animate-fadeIn" style={{ animationDelay: '0.6s' }}>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-3xl sm:text-4xl font-bold text-red-400 mb-2">20+</div>
              <div className="text-base sm:text-lg">Years Experience</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-3xl sm:text-4xl font-bold text-red-400 mb-2">1000+</div>
              <div className="text-base sm:text-lg">Projects Completed</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-3xl sm:text-4xl font-bold text-red-400 mb-2">100%</div>
              <div className="text-base sm:text-lg">Client Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </section>

      {/* Services Section with Parallax */}
      <section id="services" className="py-24 bg-white relative overflow-hidden">
        {/* Subtle parallax background element */}
        <div 
          className="absolute inset-0 opacity-5"
          style={isMobile ? {} : {
            backgroundImage: 'url(/fcbghero1.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${(scrollY - 800) * 0.2}px)`
          }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-blue-900">Our</span>
              <span className="text-red-600"> Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive commercial construction solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Commercial Buildings',
                description: 'From office spaces to retail centers, we construct modern commercial buildings that meet your business needs.',
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                )
              },
              {
                title: 'Industrial Warehouses',
                description: 'Durable, efficient warehouse construction designed for manufacturing and distribution operations.',
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                  </svg>
                )
              },
              {
                title: 'Healthcare Facilities',
                description: 'Specialized construction for hospitals, clinics, and medical centers with strict compliance standards.',
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                  </svg>
                )
              },
              {
                title: 'Concrete & Foundation',
                description: 'Expert concrete work and foundation services ensuring structural integrity for decades.',
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                )
              },
              {
                title: 'Interior Build-Outs',
                description: 'Transform your space with professional interior construction and renovation services.',
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
                  </svg>
                )
              },
              {
                title: 'Project Management',
                description: 'End-to-end project coordination ensuring on-time, on-budget delivery of your construction project.',
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                  </svg>
                )
              }
            ].map((service, index) => (
              <div key={index} className="group bg-slate-50 rounded-2xl p-8 hover:bg-blue-900 transition-all duration-300 hover:shadow-2xl hover:scale-105 cursor-pointer">
                <div className="text-blue-900 group-hover:text-white transition-colors mb-4">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-blue-900 group-hover:text-white mb-3 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-100 transition-colors">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section with Parallax Background */}
      <section id="portfolio" className="py-24 bg-slate-100 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={isMobile ? {} : {
            backgroundImage: 'url(/fcbghero1.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${(scrollY - 1400) * 0.15}px)`
          }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-blue-900">Featured</span>
              <span className="text-red-600"> Projects</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A showcase of our commitment to excellence and American craftsmanship
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Downtown Chicago Office Complex',
                category: 'Commercial',
                description: '120,000 sq ft modern office building in the heart of Chicago\'s business district',
                stats: ['18 months', '15 stories', '$45M value'],
                image: '/office2.png'
              },
              {
                title: 'Westmont Corporate Campus',
                category: 'Commercial',
                description: 'Modern multi-tenant office complex with sustainable design features and premium amenities',
                stats: ['16 months', '95,000 sq ft', '$38M value'],
                image: '/office1.jpeg'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer transform hover:-translate-y-2">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {project.category}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-3">{project.title}</h3>
                  <p className="text-gray-600 mb-6">{project.description}</p>
                  <div className="flex flex-wrap gap-4">
                    {project.stats.map((stat, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        {stat}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-blue-900">Why Choose</span>
                <span className="text-red-600"> Freedom Contracting?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                For over 20 years, we've been building more than structures—we've been building trust, relationships, and the future of Chicago's commercial landscape.
              </p>
              <div className="space-y-6">
                {[
                  {
                    title: 'American Values',
                    description: 'Integrity, hard work, and commitment to excellence in everything we do'
                  },
                  {
                    title: 'Licensed & Insured',
                    description: 'Fully certified, bonded, and compliant with all regulations'
                  },
                  {
                    title: 'Safety First',
                    description: 'Zero-accident safety record with OSHA-certified teams'
                  },
                  {
                    title: 'On-Time Delivery',
                    description: 'Proven track record of meeting deadlines and budgets'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4 group cursor-pointer">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-600 group-hover:bg-blue-900 rounded-lg flex items-center justify-center transition-colors duration-300">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2 group-hover:text-red-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img 
                  src="/fcbghero1.webp" 
                  alt="Freedom Contracting team at work"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-blue-900 text-white p-8 rounded-2xl shadow-2xl max-w-xs transform hover:scale-105 transition-transform duration-300">
                <p className="text-4xl font-bold mb-2">1000+</p>
                <p className="text-lg">Successful projects across Chicagoland</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-900 via-gray-900 to-red-900 text-white relative overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
          }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Let's discuss how we can bring your vision to life with quality craftsmanship and American values
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                setContactModalOpen(true);
                if (window.dataLayer) {
                  window.dataLayer.push({
                    event: 'contact_modal_opened',
                    page_title: 'Contact Us',
                    button_location: 'CTA Section'
                  });
                }
              }}
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-2xl transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Get a Free Consultation
            </button>
            <a 
              href="tel:+13126756000"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold py-4 px-8 rounded-xl transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-950 via-gray-900 to-red-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <img 
                    src="/logo2.webp" 
                    alt="Freedom Contracting Group Logo" 
                    className="h-20 w-auto object-contain" 
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    <span className="text-red-400">Freedom</span>
                    <span className="text-white"> Contracting Group</span>
                  </h3>
                  <p className="text-sm text-gray-300">Built on American Values</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Proudly serving Chicago and surrounding areas with commercial construction excellence. 
                We believe in American craftsmanship, integrity, and building structures that stand the test of time.
              </p>
              
              <div className="flex space-x-4">
                <a href="https://www.facebook.com/profile.php?id=61584869879330" onClick={() => trackSocialClick('facebook', 'https://www.facebook.com/profile.php?id=61584869879330')} className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg hover:scale-110 transform duration-300">
                  <span className="text-white text-sm font-bold">f</span>
                </a>
                <a href="https://www.linkedin.com/company/freedom-contracting-group/" className="w-10 h-10 bg-blue-700 rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors shadow-lg hover:scale-110 transform duration-300">
                  <span className="text-white text-xs font-bold">in</span>
                </a>
                <a href="https://www.instagram.com/freedom_contracting_group/" className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center hover:from-purple-700 hover:to-pink-700 transition-colors shadow-lg hover:scale-110 transform duration-300">
                  <span className="text-white text-xs font-bold">ig</span>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-red-400">Our Services</h4>
              <ul className="space-y-3 text-gray-300">
                {['Office Buildings', 'Industrial Warehouses', 'Retail Centers', 'Healthcare Facilities', 'Project Management'].map((service, idx) => (
                  <li key={idx} className="hover:text-white transition-colors cursor-pointer flex items-center gap-2 hover:translate-x-2 transform duration-300">
                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                    {service}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4 text-red-400">Contact Info</h4>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  <div>
                    <p className="font-medium">111 Jackson Suite 1700</p>
                    <p>Chicago, IL 60604</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 hover:translate-x-2 transform transition-transform duration-300">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  <a href="tel:+13126756000" className="hover:text-white transition-colors">(312) 675-6000</a>
                </div>
                <div className="flex items-center gap-3 hover:translate-x-2 transform transition-transform duration-300">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <a href="mailto:hello@freedomcg.net" onClick={() => trackEmailClick('hello@freedomcg.net', 'footer')}className="hover:text-white transition-colors">hello@freedomcg.net</a>
                </div>
                <div className="pt-2">
                  <span className="inline-flex items-center gap-2 bg-green-600/20 text-green-300 px-3 py-1 rounded-full text-xs font-semibold">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Licensed • Bonded • Insured
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-12 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-center sm:text-left">
                2026 Freedom Contracting Group. Proudly building America's future, one project at a time.
              </p>
              <div className="flex gap-6 text-sm">
                <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
                <span className="text-gray-600">•</span>
                <a href="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>



      {/* Contact Form Modal */}
      {contactModalOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setContactModalOpen(false)}
        >
          <div 
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setContactModalOpen(false)}
                className="absolute -top-4 -right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
              <ContactForm />
            </div>
          </div>
        </div>
      )}
     {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}

export default App;